import React, {forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import './SupplyPanel.css';
import {
  agrarianismSelectFarmForConversion,
  declineSupplyAutoFarm,
  deselectFarm,
  giantsResetAdditionalSupplySelection,
  selectFarm,
  showToast,
  undoUnitPendingSupply
} from '../../../redux/actions';
import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectGiantsSelectedUnitId,
  selectHexes,
  selectIsAgrarianismConversionOn,
  selectPendingSupply,
  selectRound,
  selectSelectedFarmsForConversion,
  selectSpecialBuildings,
  selectUnits
} from '../../../redux/reducers/sharedStateReducer/selectors';
import {
  selectIsFarmAutoSelected,
  selectSelectedFarmHexKey,
  selectSupplyAutoFarmDeclinedTurn
} from '../../../redux/reducers/privateStateReducer/selectors';
import {
  selectIsParallelSupplyPhase,
  selectSupplyActingPlayer
} from '../../../utils/gameState/supply/parallelSupplySelectors';
import {
  buildSupplyPanelModel,
  getSupplyTurnKey,
  pickAutoSupplyFarm,
  pickSupplyFarmForUnit
} from '../../../utils/gameState/supply/buildSupplyPanelModel';
import {getPlayerColorStyle} from '../../../utils/gameUi/getPlayerColorStyle';
import {checkIfColorBlendsWithStone} from '../../../utils/gameUi/checkIfColorBlendsWithStone';
import {getUnitIconClassNameForButton} from '../../../utils/unit/getUnitIconClassNameForButton';
import {getUnitIconDisplayProps} from '../../../utils/factions/succubes/getUnitIconDisplayProps';
import {getFarmTokenImage} from '../../../configs/farmTokenImages';
import {REGISTRY_SINGLETON} from '../../../scenes/Registry';
import {joinClassNames} from '../../gameUi/joinClassNames';
import {HexButtonFrame} from '../../gameUi/HexButton';
import UnitIcon from '../NewUnitToolbar/UnitIcon/UnitIcon';
import RollingValue from '../NewUnitToolbar/RecruitUnitCard/RollingValue';
import {getTokenRowSlots, TokenGlint, TokenSpotlight} from '../TokenMuster/TokenMuster';
import SupplyRedistributeButton from '../SupplyRedistributeButton/SupplyRedistributeButton';

const SUPPLY_ICON = '/img/icons/supply.png';
// Столько зерен великану хватит: дальше счетчик
const PIPS_MAX = 2;

const formatSigned = (value) => (value > 0 ? `+${value}` : `${value}`.replace('-', '−'));

/**
 * Ряд жетонов: прокручивается вбок, если не уместился. Край гаснет с той стороны, куда еще
 * можно прокрутить. Колесо мыши крутит ряд: полосы прокрутки у него нет
 */
const SupplyRow = forwardRef(({className, children}, forwardedRef) => {
  const rowRef = useRef(null);
  const [fade, setFade] = useState({left: false, right: false});

  const measure = useCallback(() => {
    const row = rowRef.current;
    if (!row) {
      return;
    }
    const left = row.scrollLeft > 1;
    const right = row.scrollLeft + row.clientWidth < row.scrollWidth - 1;
    setFade((previous) => (previous.left === left && previous.right === right ? previous : {left, right}));
  }, []);

  useLayoutEffect(() => {
    measure();
    if (typeof ResizeObserver !== 'function' || !rowRef.current) {
      return undefined;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(rowRef.current);
    return () => observer.disconnect();
  }, [measure, children]);

  const handleWheel = (event) => {
    const row = rowRef.current;
    if (row && row.scrollWidth > row.clientWidth && event.deltaY !== 0) {
      row.scrollLeft += event.deltaY;
    }
  };

  return (
    <div
      ref={(node) => {
        rowRef.current = node;
        if (forwardedRef) {
          forwardedRef.current = node;
        }
      }}
      className={joinClassNames(
        'supply-panel_row',
        className,
        fade.left && 'supply-panel_row_fade-left',
        fade.right && 'supply-panel_row_fade-right',
      )}
      onScroll={measure}
      onWheel={handleWheel}
    >
      {children}
    </div>
  );
});

/** Деление шкалы снабжения: заливается сдвигом своего слоя, а плита не перерисовывается */
const SupplyMeterCell = ({isFilled, index}) => (
  <span className={joinClassNames('supply-meter_cell', isFilled && 'supply-meter_cell_filled')} style={{'--cell-index': index}}>
    <span className={'supply-meter_fill'}/>
  </span>
);

/** Показатель сводки: значение и подпись под ним. С onClick это кнопка */
const SupplyStat = ({value, caption, tone, title, onClick}) => {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      className={joinClassNames('supply-stat', tone && `supply-stat_${tone}`)}
      title={title}
      onClick={onClick}
    >
      <span className={'supply-stat_value'}>{value}</span>
      <span className={'supply-stat_caption'}>{caption}</span>
    </Tag>
  );
};

/** Итог по всей раздаче: сколько роздано и что это даст армии */
const SupplyOverview = ({totals, onHungryClick}) => (
  <div className={'supply-overview'}>
    <div className={'supply-overview_head'}>
      <span className={'supply-overview_label'}>Роздано</span>
      <span className={'supply-overview_count'}>
        <RollingValue value={totals.distributed} rank={totals.distributed}/>
        <span className={'supply-overview_of'}>/{totals.produced}</span>
        <img src={SUPPLY_ICON} alt={'Снабжение'}/>
      </span>
    </div>
    <div className={'supply-meter'} role={'img'} aria-label={`Роздано ${totals.distributed} снабжения из ${totals.produced}`}>
      {Array.from({length: totals.produced}, (_, index) => (
        <SupplyMeterCell key={index} index={index} isFilled={index < totals.distributed}/>
      ))}
    </div>
    <div className={'supply-overview_stats'}>
      <SupplyStat
        value={`${totals.suppliedCount}/${totals.unitsCount}`}
        caption={'снабжено'}
        title={'Отряды, получившие снабжение'}
      />
      <SupplyStat
        value={totals.additionalCount}
        caption={'доп.'}
        title={'Отряды с дополнительным снабжением'}
      />
      <SupplyStat
        value={totals.hungryCount}
        caption={'голод'}
        tone={totals.hungryCount > 0 ? 'bad' : undefined}
        title={totals.hungryCount > 0 ? 'Показать отряд без снабжения' : 'Голодных отрядов нет'}
        onClick={totals.hungryCount > 0 ? onHungryClick : undefined}
      />
      <SupplyStat
        value={formatSigned(totals.armyUpdate)}
        caption={'армия'}
        tone={totals.armyUpdate < 0 ? 'bad' : totals.armyUpdate > 0 ? 'good' : undefined}
        title={'Как изменится суммарный размер отрядов'}
      />
    </div>
  </div>
);

/**
 * Ферма с нерозданным снабжением, как на поле: сколько колосков, столько она дает, белые — еще
 * не розданные. Нажатие выбирает ее, в обмене отдает единицу на монеты
 */
const SupplyFarmToken = ({farm, slot, onClick}) => (
  <div
    className={joinClassNames('token-muster_item', 'supply-farm', farm.takersCount === 0 && 'supply-farm_idle')}
    style={{'--slot-index': slot}}
    data-supply-farm={farm.hexKey}
  >
    <button
      type={'button'}
      className={joinClassNames('gui-hex-button', 'gui-hex-button_stone', 'toolbar-button', farm.isSelected && 'gui-hex-button_selected')}
      aria-pressed={farm.isSelected}
      title={`Ферма: не роздано ${farm.remaining} из ${farm.capacity}`}
      onClick={onClick}
    >
      <img src={getFarmTokenImage(farm.remaining, farm.capacity)} alt={'Ферма'}/>
      {farm.isSelected ? <HexButtonFrame className={'cell-icon'}/> : null}
      <TokenGlint/>
    </button>
  </div>
);

const getUnitTokenTitle = ({unit, sizeUpdate, sizeAfter, isHungry, takesSupply, reachableFarmHexKeys}) => {
  const sizeText = sizeAfter <= 0
    ? `размер ${unit.size} → отряд погибнет`
    : sizeUpdate === 0 ? `размер ${unit.size}` : `размер ${unit.size} → ${sizeAfter}`;
  if (!takesSupply) {
    return `Снабжен полностью, ${sizeText}`;
  }
  if (reachableFarmHexKeys.length === 0) {
    return isHungry
      ? `Без снабжения, ${sizeText}. Ни одна ферма с остатком не дотягивается`
      : `Снабжен, ${sizeText}. Ни одна ферма с остатком не дотягивается`;
  }
  return isHungry ? `Без снабжения, ${sizeText}. Нажмите, чтобы снабдить` : `Снабжен, ${sizeText}. Нажмите, чтобы снабдить дополнительно`;
};

/** Зерна на жетоне: базовое и дополнительное снабжение. У великана базовых сколько угодно */
const SupplyPips = ({basicCount, hasAdditional, isGiant}) => {
  if (isGiant && basicCount > PIPS_MAX) {
    return <span className={'supply-unit_pips supply-unit_pips_count'}>×{basicCount}</span>;
  }
  const basicPips = isGiant ? Math.max(basicCount, 1) : 1;
  return (
    <span className={'supply-unit_pips'} aria-hidden={true}>
      {Array.from({length: basicPips}, (_, index) => (
        <span key={index} className={joinClassNames('supply-unit_pip', index < basicCount && 'supply-unit_pip_filled')}/>
      ))}
      <span className={joinClassNames('supply-unit_pip', 'supply-unit_pip_additional', hasAdditional && 'supply-unit_pip_filled')}/>
    </span>
  );
};

/** Стрелка «было → стало»: с древком, чтобы не читалась как знак «больше» */
const SupplySizeArrow = () => (
  <svg className={'supply-unit_size-arrow'} viewBox={'0 0 10 10'} aria-hidden={true}>
    <path d={'M0 3.2H4.6V0L10 5L4.6 10V6.8H0Z'} fill={'currentColor'}/>
  </svg>
);

/** Отряд: нажатие дает снабжение, «−» снимает последнее */
const SupplyUnitToken = ({entry, iconProps, slot, isDimmed, isReachable, onGive, onUndo, onPreview}) => {
  const {unit, sizeUpdate, sizeAfter, isHungry, basicCount, hasAdditional, isGiant, isUndoable} = entry;
  return (
    <div
      className={joinClassNames(
        'token-muster_item',
        'supply-unit',
        isHungry && 'supply-unit_hungry',
        isDimmed && 'supply-unit_dimmed',
        isReachable && 'supply-unit_reachable',
        !entry.takesSupply && 'supply-unit_full',
      )}
      style={{'--slot-index': slot, ...getPlayerColorStyle(iconProps.playerColor)}}
      data-supply-unit={unit.id}
    >
      <span className={'supply-unit_alarm'} aria-hidden={true}/>
      <button
        type={'button'}
        className={'gui-hex-button gui-hex-button_stone toolbar-button'}
        title={getUnitTokenTitle(entry)}
        onClick={onGive}
        onPointerEnter={onPreview}
      >
        <div className={checkIfColorBlendsWithStone(iconProps.playerColor) ? 'supply-unit_outline' : undefined}>
          <UnitIcon {...iconProps} unitType={unit.type} className={getUnitIconClassNameForButton(unit.type)}/>
        </div>
        <TokenGlint/>
        {/* Вспышка при каждой выдаче: новый ключ запускает анимацию заново */}
        <span key={`flash-${basicCount}-${hasAdditional}`} className={'supply-unit_flash'} aria-hidden={true}/>
        <span className={joinClassNames(
          'token-muster_price',
          'supply-unit_size',
          sizeUpdate > 0 && 'supply-unit_size_up',
          sizeUpdate < 0 && 'supply-unit_size_down',
        )}>
          {sizeUpdate !== 0 && <><span className={'supply-unit_size-before'}>{unit.size}</span><SupplySizeArrow/></>}
          <RollingValue value={sizeAfter} rank={sizeAfter}/>
        </span>
      </button>
      <SupplyPips basicCount={basicCount} hasAdditional={hasAdditional} isGiant={isGiant}/>
      {isUndoable && (
        <button
          type={'button'}
          className={'supply-unit_undo'}
          title={'Снять снабжение'}
          aria-label={'Снять снабжение'}
          onClick={onUndo}
        >
          −
        </button>
      )}
    </div>
  );
};

/**
 * Панель фазы снабжения. Слева итог всей раздачи, дальше фермы, с которых еще есть что
 * раздать, и отряды. Жетон отряда снабжает с выделенной фермы; те же действия, что и клики по
 * полю. Пока игрок сам не отказался, ферму выделяет панель: первую и следующую за опустевшей,
 * без движения камеры. Выделенную так ферму жетон может сменить на ту, что дотягивается. Осмотр
 * ячейки или снятие выбора — отказ: тогда ферму выбирает игрок. Плита по содержимому: растет
 * влево, правый край на месте
 *
 * @param {React.ReactNode} [children] - кнопки фракции и политики
 */
const SupplyPanel = ({children}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const supplyPlayer = useSelector(selectSupplyActingPlayer);
  const isParallelSupplyPhase = useSelector(selectIsParallelSupplyPhase);
  const actorPlayerId = isParallelSupplyPhase ? supplyPlayer?.id : undefined;
  const isConversionOn = useSelector((state) => selectIsAgrarianismConversionOn(state, supplyPlayer?.id ?? null));
  const selectedFarmsForConversion = useSelector((state) => selectSelectedFarmsForConversion(state, supplyPlayer?.id ?? null));
  const selectedGiantId = useSelector((state) => selectGiantsSelectedUnitId(state, supplyPlayer?.id ?? null));
  const selectedFarmHexKey = useSelector(selectSelectedFarmHexKey);
  const isFarmAutoSelected = useSelector(selectIsFarmAutoSelected);
  const declinedTurn = useSelector(selectSupplyAutoFarmDeclinedTurn);
  const round = useSelector(selectRound);
  const units = useSelector(selectUnits);
  const hexes = useSelector(selectHexes);
  const pendingSupply = useSelector(selectPendingSupply);
  const specialBuildings = useSelector(selectSpecialBuildings);
  const buildingsDamage = useSelector(selectBuildingsDamage);
  const citiesCache = useSelector(selectCitiesCache);
  const unitsRowRef = useRef(null);

  // Обход ферм и путей до отрядов — только когда что-то из этого поменялось
  const model = useMemo(
    () => (supplyPlayer ? buildSupplyPanelModel(store.getState(), supplyPlayer) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supplyPlayer, units, hexes, pendingSupply, specialBuildings, buildingsDamage, citiesCache, selectedFarmHexKey, selectedFarmsForConversion]
  );

  const turnKey = supplyPlayer ? getSupplyTurnKey(round, supplyPlayer.id) : null;
  const isAutoFarmDeclined = declinedTurn === turnKey;

  useEffect(() => {
    if (!model || selectedFarmHexKey || isConversionOn || isAutoFarmDeclined) {
      return;
    }
    const farmHexKey = pickAutoSupplyFarm(model);
    if (farmHexKey) {
      dispatch(selectFarm(farmHexKey, {isAuto: true}));
    }
  }, [dispatch, model, selectedFarmHexKey, isConversionOn, isAutoFarmDeclined]);

  if (!model) {
    return null;
  }

  const scene = REGISTRY_SINGLETON.getGameScene();
  const soundManager = REGISTRY_SINGLETON.getSoundManager();
  const farmSlots = getTokenRowSlots(model.farms.map(({hexKey}) => ({id: hexKey})));
  const selectedFarm = model.farms.find(({isSelected}) => isSelected);

  const handleFarmClick = (farm) => {
    if (isConversionOn) {
      dispatch(agrarianismSelectFarmForConversion(farm.hexKey, actorPlayerId));
      soundManager.playDistributeSupply();
      return;
    }
    if (selectedGiantId) {
      dispatch(giantsResetAdditionalSupplySelection(actorPlayerId));
    }
    if (farm.isSelected) {
      dispatch(deselectFarm());
      dispatch(declineSupplyAutoFarm(turnKey));
      return;
    }
    dispatch(selectFarm(farm.hexKey));
    soundManager.playSelectFarm();
    scene?.focusCameraOnSupplyHex?.(farm.hexKey);
  };

  // С какой фермы снабдить отряд сейчас: выделенная, если дотягивается. Выделенную панелью можно
  // сменить на другую, а выбор игрока — нет. Без выделенной фермы выбирает игрок
  const resolveUnitFarm = (entry) => {
    if (!selectedFarm) {
      return null;
    }
    if (entry.reachableFarmHexKeys.includes(selectedFarm.hexKey)) {
      return selectedFarm.hexKey;
    }
    return isFarmAutoSelected ? pickSupplyFarmForUnit(model, entry) : null;
  };

  // Выделение едет за отрядом: путь на поле и подсвеченная ферма должны совпадать
  const followUnitFarm = (farmHexKey) => {
    if (farmHexKey !== selectedFarm?.hexKey) {
      dispatch(selectFarm(farmHexKey, {isAuto: true}));
    }
  };

  const showUnitGiveRefusal = (entry) => {
    if (selectedFarm) {
      scene?.showBlockedSupplyPathFromPanel?.(selectedFarm.hexKey, entry.unit.hexKey);
    }
    if (model.totals.remaining <= 0) {
      dispatch(showToast('Снабжение роздано. Снимите его с другого отряда кнопкой «−».', 'warning', 4000));
    } else if (entry.reachableFarmHexKeys.length === 0) {
      dispatch(showToast('До этого отряда не дотягивается ни одна ферма, с которой еще есть что раздать.', 'warning', 4000));
      scene?.focusCameraOnSupplyHex?.(entry.unit.hexKey);
    } else if (selectedFarm) {
      dispatch(showToast('Выбранная ферма не дотягивается до этого отряда. Выберите другую.', 'warning', 4000));
    } else {
      dispatch(showToast('Выберите ферму, с которой отдать снабжение этому отряду.', 'warning', 4000));
    }
  };

  const handleUnitGive = (entry) => {
    if (!entry.takesSupply) {
      dispatch(showToast('Отряд уже получил базовое и дополнительное снабжение!', 'warning', 3000));
      return;
    }
    const farmHexKey = resolveUnitFarm(entry);
    if (!farmHexKey) {
      showUnitGiveRefusal(entry);
      return;
    }
    followUnitFarm(farmHexKey);
    scene?.giveSupplyFromPanel?.(farmHexKey, entry.unit.id);
  };

  const handleUnitUndo = (entry) => {
    dispatch(undoUnitPendingSupply(entry.unit.id, actorPlayerId));
    soundManager.playUndoClick();
  };

  const handleUnitPreview = (entry, event) => {
    // Только мышью: у пальца наведения нет, а нажатие покажет путь само
    if (event.pointerType !== 'mouse' || !entry.takesSupply) {
      return;
    }
    const farmHexKey = resolveUnitFarm(entry);
    if (farmHexKey) {
      followUnitFarm(farmHexKey);
      scene?.previewSupplyPathFromPanel?.(farmHexKey, entry.unit.hexKey);
    }
  };

  const handleHungryClick = () => {
    const hungry = model.units.find(({isHungry}) => isHungry);
    if (!hungry) {
      return;
    }
    unitsRowRef.current?.querySelector(`[data-supply-unit="${hungry.unit.id}"]`)?.scrollIntoView?.({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
    scene?.focusCameraOnSupplyHex?.(hungry.unit.hexKey);
  };

  const isPlayerFarmsBlocked = model.totals.produced === 0;

  return (
    <div className={joinClassNames('supply-panel', isConversionOn && 'supply-panel_conversion')} style={getPlayerColorStyle(supplyPlayer.color)}>
      <span className={'supply-panel_sweep'} aria-hidden={true}/>
      <SupplyOverview totals={model.totals} onHungryClick={handleHungryClick}/>
      <span className={'supply-panel_divider supply-panel_divider_a'} aria-hidden={true}/>
      <div className={'supply-panel_farms'}>
        {model.farms.length > 0 ? (
          <SupplyRow>
            <div className={'token-muster'}>
              <TokenSpotlight spot={selectedFarm ? farmSlots[selectedFarm.hexKey] : undefined}/>
              {model.farms.map((farm, index) => (
                <SupplyFarmToken
                  key={farm.hexKey}
                  farm={farm}
                  slot={index}
                  onClick={() => handleFarmClick(farm)}
                />
              ))}
            </div>
          </SupplyRow>
        ) : (
          <div className={'supply-panel_farms-done'}>
            {isPlayerFarmsBlocked ? 'Снабжения нет' : 'Всё роздано'}
          </div>
        )}
        {!isConversionOn && <SupplyRedistributeButton disabled={!model.units.some(({isUndoable}) => isUndoable)}/>}
      </div>
      {!isConversionOn && (
        <>
          <span className={'supply-panel_divider supply-panel_divider_b'} aria-hidden={true}/>
          <SupplyRow className={'supply-panel_units'} ref={unitsRowRef}>
            {model.units.length > 0 ? (
              <div className={'token-muster'}>
                {model.units.map((entry, index) => (
                  <SupplyUnitToken
                    key={entry.unit.id}
                    entry={entry}
                    iconProps={getUnitIconDisplayProps(entry.unit, supplyPlayer, [supplyPlayer])}
                    slot={index}
                    isDimmed={Boolean(selectedFarm) && !isFarmAutoSelected && !entry.reachableFarmHexKeys.includes(selectedFarm.hexKey)}
                    isReachable={Boolean(selectedFarm) && entry.takesSupply && entry.reachableFarmHexKeys.includes(selectedFarm.hexKey)}
                    onGive={() => handleUnitGive(entry)}
                    onUndo={() => handleUnitUndo(entry)}
                    onPreview={(event) => handleUnitPreview(entry, event)}
                  />
                ))}
              </div>
            ) : (
              <div className={'supply-panel_farms-done'}>Отрядов нет</div>
            )}
          </SupplyRow>
        </>
      )}
      {React.Children.toArray(children).length > 0 && <div className={'supply-panel_actions'}>{children}</div>}
    </div>
  );
};

export default SupplyPanel;
